"use client";

import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateMultiPickerProps {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  minDate?: string;
}

export function DateMultiPicker({
  value,
  onChange,
  className,
  minDate,
}: DateMultiPickerProps) {
  const selected = value.map((d) => parseISO(d));
  const label =
    value.length === 0
      ? "Pick dates"
      : value.length === 1
        ? format(selected[0], "EEE, d MMM yyyy")
        : `${value.length} dates selected`;

  function removeDate(dateStr: string) {
    onChange(value.filter((d) => d !== dateStr));
  }

  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              value.length === 0 && "text-muted-foreground",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="multiple"
            selected={selected}
            disabled={
              minDate
                ? (day) => format(day, "yyyy-MM-dd") < minDate
                : undefined
            }
            onSelect={(days) => {
              const next = (days ?? [])
                .map((d) => format(d, "yyyy-MM-dd"))
                .filter((d) => !minDate || d >= minDate)
                .sort();
              onChange(next);
            }}
          />
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1 rounded-full border border-saffron-200 bg-saffron-50 px-2 py-0.5 text-xs"
            >
              {format(parseISO(d), "d MMM")}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-saffron-100"
                onClick={() => removeDate(d)}
                aria-label={`Remove ${d}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
